import { Outlet } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { Wifi, Signal, Shield, Zap, BarChart3, Users } from 'lucide-react'

const FEATURES = [
  { icon: Signal,   title: 'Live Tracking',        desc: 'Real-time installation & ticket updates' },
  { icon: Shield,   title: 'Role-based Access',     desc: 'Admin, engineer & supervisor permissions' },
  { icon: Zap,      title: 'Task Management',       desc: 'Create, assign and track field tasks' },
  { icon: BarChart3, title: 'Analytics',            desc: 'SLA compliance and performance reports' },
  { icon: Users,    title: 'Team Management',       desc: 'Manage field engineers and zones' },
]

const AuthLayout = () => {
  return (
    <div className="min-h-screen flex bg-[#f0f4fa]">

      {/* ── Left hero panel ── */}
      <div className="hidden lg:flex lg:w-[52%] relative overflow-hidden flex-col"
        style={{ background: 'linear-gradient(145deg, #050d1f 0%, #0a1628 40%, #0d2040 70%, #071525 100%)' }}>

        {/* Animated mesh orbs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full opacity-30"
            style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.5) 0%, transparent 70%)', filter: 'blur(60px)' }} />
          <div className="absolute top-1/3 -right-20 w-[400px] h-[400px] rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.6) 0%, transparent 70%)', filter: 'blur(50px)' }} />
          <div className="absolute -bottom-20 left-1/4 w-[350px] h-[350px] rounded-full opacity-25"
            style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.5) 0%, transparent 70%)', filter: 'blur(55px)' }} />
          {/* Grid overlay */}
          <div className="absolute inset-0 opacity-[0.04]"
            style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
        </div>

        {/* Logo */}
        <div className="relative z-10 px-12 pt-10">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #3b82f6, #06b6d4)', boxShadow: '0 0 24px rgba(59,130,246,0.5)' }}>
              <Wifi size={22} className="text-white" />
            </div>
            <div>
              <p className="font-extrabold text-white text-lg tracking-wide leading-none">BSNL FTTH</p>
              <p className="text-blue-300/60 text-[10px] font-medium mt-0.5">Support Platform</p>
            </div>
          </div>
        </div>

        {/* Hero text */}
        <div className="relative z-10 flex-1 flex flex-col justify-center px-12">
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-3.5 py-1.5 mb-6">
              <span className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse" />
              <span className="text-blue-300 text-xs font-semibold">Field Operations Platform</span>
            </div>
            <h1 className="text-5xl font-black text-white leading-[1.1] tracking-tight">
              Manage Every<br />
              <span style={{ background: 'linear-gradient(135deg, #60a5fa, #22d3ee)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                Connection.
              </span>
            </h1>
            <p className="text-slate-400 text-base mt-5 max-w-sm leading-relaxed">
              The all-in-one platform for BSNL FTTH installations, field support, and real-time operations management.
            </p>
          </div>

          {/* Feature cards */}
          <div className="grid grid-cols-2 gap-3">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title}
                className="p-4 rounded-2xl group hover:scale-[1.02] transition-transform duration-200"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="w-8 h-8 rounded-xl flex items-center justify-center mb-2.5"
                  style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.2)' }}>
                  <Icon size={15} className="text-blue-400" />
                </div>
                <p className="text-white text-sm font-semibold leading-tight">{title}</p>
                <p className="text-slate-400 text-[11px] mt-1 leading-snug">{desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 px-12 pb-8">
          <div className="flex items-center gap-2 pt-6 border-t border-white/[0.07]">
            <div className="flex -space-x-2">
              {['R','A','P','M'].map(l => (
                <div key={l} className="w-6 h-6 rounded-full border-2 border-[#0a1628] flex items-center justify-center text-[9px] font-bold text-white"
                  style={{ background: 'linear-gradient(135deg, #6366f1, #3b82f6)' }}>{l}</div>
              ))}
            </div>
            <p className="text-slate-400 text-xs">Trusted by <span className="text-white font-semibold">500+</span> BSNL engineers</p>
          </div>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex-1 flex flex-col min-h-screen relative">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(59,130,246,0.06) 0%, transparent 50%), radial-gradient(circle at 20% 80%, rgba(6,182,212,0.05) 0%, transparent 50%)' }} />

        {/* Mobile header */}
        <div className="lg:hidden relative z-10 flex items-center gap-3 px-6 pt-8 pb-4">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #3b82f6, #06b6d4)', boxShadow: '0 0 16px rgba(59,130,246,0.35)' }}>
            <Wifi size={20} className="text-white" />
          </div>
          <div>
            <p className="font-extrabold text-gray-900 text-base">BSNL FTTH</p>
            <p className="text-gray-400 text-xs">Support Platform</p>
          </div>
        </div>

        {/* Form */}
        <div className="relative z-10 flex-1 flex flex-col justify-center px-8 py-8 max-w-md w-full mx-auto lg:mx-0 lg:ml-auto lg:mr-16 xl:mr-24">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-full px-3 py-1 mb-5">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
              <span className="text-blue-700 text-xs font-semibold">Secure Sign In</span>
            </div>
            <h1 className="text-3xl font-black text-gray-900 leading-tight">Welcome back</h1>
            <p className="text-gray-500 text-sm mt-2">Sign in to access your operations dashboard</p>
          </div>

          <Outlet />
        </div>

        {/* Footer */}
        <div className="relative z-10 text-center pb-6 px-6">
          <p className="text-gray-400 text-xs">© {new Date().getFullYear()} Worldshore Technologies · Toll Free: 1800-345-1500</p>
        </div>
      </div>

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3500,
          style: {
            background: '#0f172a',
            color: '#f1f5f9',
            borderRadius: '14px',
            padding: '12px 16px',
            fontSize: '13px',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
          }
        }}
      />
    </div>
  )
}

export default AuthLayout
